import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  TextField,
  Stack,
  Chip,
} from '@mui/material';
import {
  Language as OnlineIcon,
  LocalShipping as DeliveryIcon,
  Warning as WarningIcon,
  PauseCircle as PauseIcon,
} from '@mui/icons-material';
import { BottomSheet, BottomSheetActions } from './BottomSheet';
import { HelpTooltip } from '../Onboarding';
import { useTimeFormat } from '../../context/TimeFormatContext';
import { formatSnoozeDateTime } from '../../utils/formatTime';
import { MobileStorePicker, type MobileStore } from './MobileStorePicker';
import { useStoreManagement } from '../../context/StoreManagementContext';
import { useStoresQuery, useInvalidateStores } from '../../hooks/queries';
import { getStoreChannels, updateStoreOnlineOrders, toggleChannelStatus } from '../../api';
import type { ChannelSettings, DeliveryAggregator } from '../../types/channels';
import { CHANNEL_SNOOZE_PRESETS, DEFAULT_SNOOZE_PRESET, nextFiveAM } from '../StoreManagement/channelSnoozePresets';

// Channel logo mapping
const channelLogos: Record<string, string> = {
  UberEats: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6a/UberEatsLogo.svg/120px-UberEatsLogo.svg.png',
  Deliveroo: 'https://upload.wikimedia.org/wikipedia/en/thumb/6/6b/Deliveroo_Logo.svg/120px-Deliveroo_Logo.svg.png',
  JustEat: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a4/Just_Eat_logo.svg/120px-Just_Eat_logo.svg.png',
  DoorDash: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e1/DoorDash_Logo.svg/120px-DoorDash_Logo.svg.png',
};

const getChannelColor = (name: string): string => {
  const colors: Record<string, string> = {
    UberEats: '#06C167',
    Deliveroo: '#00CCBC',
    JustEat: '#FF8000',
    DoorDash: '#FF3008',
  };
  return colors[name] || '#662C8B';
};

export const MobileChannels: React.FC = () => {
  const { use24HourFormat } = useTimeFormat();
  const { data: stores = [], isLoading: storesLoading } = useStoresQuery();
  const invalidateStores = useInvalidateStores();
  const { selectedMobileStore, setSelectedMobileStore } = useStoreManagement();
  const selectedStoreNumber = selectedMobileStore;
  const setSelectedStoreNumber = setSelectedMobileStore;
  const [channelSettings, setChannelSettings] = useState<ChannelSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Pause dialog state
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState<DeliveryAggregator | null>(null);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseExpiry, setPauseExpiry] = useState<Date | null>(null);
  const [selectedQuickOption, setSelectedQuickOption] = useState<string>('');

  // Online Orders confirmation dialog state
  const [onlineOrdersConfirmOpen, setOnlineOrdersConfirmOpen] = useState(false);
  const [pendingOnlineOrdersValue, setPendingOnlineOrdersValue] = useState<boolean>(false);
  // Disabling Online Orders is never indefinite — it always carries a
  // reactivate-at time (same presets/ceiling as an aggregator pause).
  const [ooReactivateAt, setOoReactivateAt] = useState<Date | null>(null);
  const [ooQuickOption, setOoQuickOption] = useState<string>('');

  // Convert stores to mobile format
  const mobileStores: MobileStore[] = stores.map(s => ({
    storeNumber: s.storeNumber,
    name: s.name || s.storeNumber,
    isActive: s.isActive,
  }));

  // Auto-select first store if only one
  useEffect(() => {
    if (mobileStores.length === 1 && !selectedStoreNumber) {
      setSelectedStoreNumber(mobileStores[0].storeNumber);
    }
  }, [mobileStores, selectedStoreNumber]);

  // Fetch channels when store changes
  useEffect(() => {
    if (selectedStoreNumber) {
      fetchChannels();
    }
  }, [selectedStoreNumber]);

  const fetchChannels = async () => {
    if (!selectedStoreNumber) return;

    setLoading(true);
    setError(null);
    try {
      const response = await getStoreChannels(selectedStoreNumber);
      const store = stores.find(s => s.storeNumber === selectedStoreNumber);

      if (response?.status && response?.data) {
        // Map the ByteConnect channels to our aggregators format
        const channels = response.data.channels || [];

        const getChannelDisplayName = (channelName: string) => {
          const nameMap: { [key: string]: string } = {
            'UBEREATS': 'Uber Eats',
            'DOORDASH': 'DoorDash',
            'GRUBHUB': 'Grubhub',
            'JUSTEAT': 'Just Eat',
            'DELIVEROO': 'Deliveroo',
          };
          return nameMap[channelName.toUpperCase()] || channelName;
        };

        const newSettings: ChannelSettings = {
          acceptingOnlineOrders: store?.onlineOrders ?? false,
          appearInStoreSearch: store?.appearInStoreSearch ?? true,
          onlineOrdersReactivateAt: response.data.onlineOrdersReactivateAt
            ? new Date(response.data.onlineOrdersReactivateAt)
            : undefined,
          aggregators: channels.map((channel: any) => ({
            id: channel.channel,
            name: getChannelDisplayName(channel.channel),
            isActive: channel.status === 'ONLINE',
            isArchived: false,
            deactivationReason: channel.deactivationDetails?.reason,
            markupPercent: channel.markupPercent,
          })),
        };

        setChannelSettings(newSettings);
      } else {
        setChannelSettings(null);
      }
    } catch (err) {
      console.error('Failed to fetch channels:', err);
      setError('Failed to load channels');
      setChannelSettings(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOnlineOrdersToggleRequest = (value: boolean) => {
    // Show confirmation dialog. Disabling defaults to 1H (same as aggregator
    // pauses) — the store always turns back ON automatically.
    if (!value) {
      setOoReactivateAt(DEFAULT_SNOOZE_PRESET.getValue());
      setOoQuickOption(DEFAULT_SNOOZE_PRESET.key);
    }
    setPendingOnlineOrdersValue(value);
    setOnlineOrdersConfirmOpen(true);
  };

  const handleOnlineOrdersConfirm = async () => {
    if (!selectedStoreNumber || !channelSettings) return;
    if (!pendingOnlineOrdersValue && !ooReactivateAt) return;

    setOnlineOrdersConfirmOpen(false);
    setSubmitting(true);
    setError(null);

    const requestedReactivateAtIso = !pendingOnlineOrdersValue && ooReactivateAt
      ? ooReactivateAt.toISOString()
      : undefined;

    try {
      const response = await updateStoreOnlineOrders(selectedStoreNumber, {
        acceptingOnlineOrders: pendingOnlineOrdersValue,
        reactivateAt: requestedReactivateAtIso,
      });
      // The server may clamp the requested time (5 AM Europe/London ceiling) —
      // its returned value is authoritative for the cascade expiry and display.
      const reactivateAtIso: string | undefined =
        response?.data?.reactivateAt || requestedReactivateAtIso;

      // When disabling Online Orders, cascade-pause every active aggregator so we
      // don't leave the "online OFF + aggregator ON" state Byte Commerce rejects.
      // The pauses carry the same expiry as the Online Orders reactivation, so
      // Byte Graph resumes the aggregators natively when the store turns back on.
      const aggregatorsToCascade = pendingOnlineOrdersValue
        ? []
        : channelSettings.aggregators.filter(a => a.isActive);
      for (const aggregator of aggregatorsToCascade) {
        await toggleChannelStatus(
          selectedStoreNumber,
          aggregator.id,
          false,
          'Online Orders disabled',
          reactivateAtIso
        );
      }

      setChannelSettings({
        ...channelSettings,
        acceptingOnlineOrders: pendingOnlineOrdersValue,
        onlineOrdersReactivateAt: pendingOnlineOrdersValue
          ? undefined
          : reactivateAtIso
          ? new Date(reactivateAtIso)
          : undefined,
        aggregators: pendingOnlineOrdersValue
          ? channelSettings.aggregators
          : channelSettings.aggregators.map(a =>
              a.isActive ? { ...a, isActive: false } : a
            ),
      });
      setSuccess(`Online orders ${pendingOnlineOrdersValue ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccess(null), 3000);
      invalidateStores();
    } catch (err) {
      console.error('Failed to update online orders:', err);
      setError('Failed to update online orders');
      // Refetch so the on-screen state matches what actually persisted if the
      // cascade partially succeeded.
      invalidateStores();
    } finally {
      setSubmitting(false);
    }
  };

  const handleChannelToggle = async (aggregator: DeliveryAggregator, newActive: boolean) => {
    if (!selectedStoreNumber) return;

    // If turning OFF, show pause dialog
    if (!newActive) {
      setSelectedChannel(aggregator);
      setPauseReason('');
      // Default to 1H, same as the desktop aggregator toggle and Online Orders.
      setPauseExpiry(DEFAULT_SNOOZE_PRESET.getValue());
      setSelectedQuickOption(DEFAULT_SNOOZE_PRESET.key);
      setPauseDialogOpen(true);
      return;
    }

    // Turning ON - do it directly
    setSubmitting(true);
    setError(null);

    try {
      await toggleChannelStatus(selectedStoreNumber, aggregator.id, true);

      // Update local state
      if (channelSettings) {
        const updatedAggregators = channelSettings.aggregators.map(a =>
          a.id === aggregator.id ? { ...a, isActive: true } : a
        );
        setChannelSettings({ ...channelSettings, aggregators: updatedAggregators });
      }

      setSuccess(`${aggregator.name} activated`);
      setTimeout(() => setSuccess(null), 3000);
      invalidateStores();
    } catch (err) {
      console.error('Failed to toggle channel:', err);
      setError('Failed to update channel');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseConfirm = async () => {
    if (!selectedStoreNumber || !selectedChannel || !pauseExpiry) return;

    setSubmitting(true);
    setError(null);

    try {
      await toggleChannelStatus(
        selectedStoreNumber,
        selectedChannel.id,
        false,
        pauseReason || 'Paused via Store Control',
        pauseExpiry.toISOString()
      );

      // Update local state
      if (channelSettings) {
        const updatedAggregators = channelSettings.aggregators.map(a =>
          a.id === selectedChannel.id ? { ...a, isActive: false } : a
        );
        setChannelSettings({ ...channelSettings, aggregators: updatedAggregators });
      }

      setPauseDialogOpen(false);
      setSelectedChannel(null);
      setSuccess(`${selectedChannel.name} paused. Changes may take a few minutes to process.`);
      setTimeout(() => setSuccess(null), 5000);
      invalidateStores();
    } catch (err) {
      console.error('Failed to pause channel:', err);
      setError('Failed to pause channel');
    } finally {
      setSubmitting(false);
    }
  };

  const quickExpiryOptions = CHANNEL_SNOOZE_PRESETS;

  const activeAggregators = channelSettings?.aggregators?.filter(a => !a.isArchived) || [];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      {/* Store Picker */}
      <MobileStorePicker
        stores={mobileStores}
        selectedStore={selectedStoreNumber}
        onStoreChange={setSelectedStoreNumber}
        loading={storesLoading}
      />

      {/* Alerts */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)} 
          sx={{ mx: 2, mt: 1, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}
      {success && (
        <Alert 
          severity="success" 
          onClose={() => setSuccess(null)} 
          sx={{ mx: 2, mt: 1, borderRadius: 2 }}
        >
          {success}
        </Alert>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 4 }}>
        {!selectedStoreNumber ? (
          // Empty state - welcome banner in store picker handles this
          null
        ) : loading || storesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : !channelSettings ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              Failed to load channels
            </Typography>
          </Box>
        ) : (
          <>
            {/* Online Orders Section - Compact */}
            <Box 
              sx={{ 
                bgcolor: 'background.paper', 
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                mb: 1.5,
                overflow: 'hidden',
              }}
            >
              <List disablePadding dense>
                <ListItem sx={{ py: 1, px: 2 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: channelSettings.acceptingOnlineOrders ? 'success.50' : 'grey.100', 
                        width: 32, 
                        height: 32,
                      }}
                    >
                      <OnlineIcon sx={{ color: channelSettings.acceptingOnlineOrders ? 'success.main' : 'grey.500', fontSize: 18 }} />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>Online Orders</Typography>
                        <HelpTooltip
                        title="Controls ALL online orders, including website, mobile app, and delivery aggregator ordering. To disable ONLY delivery aggregators, keep this toggle enabled, and use the individual toggles below."
                        learnMoreLink="channels/overview"
                      />
                      </Box>
                    }
                    secondary={
                      channelSettings.acceptingOnlineOrders
                        ? 'Accepting'
                        : channelSettings.onlineOrdersReactivateAt
                        ? `Paused — automatically turns back ON at ${formatSnoozeDateTime(channelSettings.onlineOrdersReactivateAt, use24HourFormat)}`
                        : 'Paused'
                    }
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: channelSettings.acceptingOnlineOrders ? 'success.main' : 'error.main',
                    }}
                  />
                  <Switch
                    edge="end"
                    checked={channelSettings.acceptingOnlineOrders}
                    onChange={(e) => handleOnlineOrdersToggleRequest(e.target.checked)}
                    disabled={submitting}
                    color="success"
                    size="small"
                  />
                </ListItem>
              </List>
            </Box>

            {/* Aggregators Section - Compact */}
            <Box 
              sx={{ 
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 0.5,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                    }}
                  >
                    Delivery Channels
                  </Typography>
                  <HelpTooltip
                    title="Toggle individual delivery platforms on/off. Paused channels won't receive orders until reactivated."
                    learnMoreLink="channels/toggle-channels"
                    iconSize={14}
                  />
                </Box>
                <Chip
                  size="small"
                  label={`${activeAggregators.filter(a => a.isActive).length}/${activeAggregators.length}`}
                  sx={{ 
                    height: 18, 
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    bgcolor: 'grey.100',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              </Box>

              <List disablePadding dense>
                {activeAggregators.map((aggregator, index) => (
                  <React.Fragment key={aggregator.id}>
                    <ListItem sx={{ py: 0.75, px: 2, minHeight: 48 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <Avatar
                          src={channelLogos[aggregator.name]}
                          sx={{
                            bgcolor: getChannelColor(aggregator.name),
                            width: 28,
                            height: 28,
                          }}
                        >
                          <DeliveryIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={aggregator.name}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        secondary={
                          !aggregator.isActive && aggregator.deactivationReason
                            ? aggregator.deactivationReason
                            : aggregator.isActive
                            ? 'Active'
                            : 'Paused'
                        }
                        secondaryTypographyProps={{ 
                          variant: 'caption',
                          color: aggregator.isActive ? 'success.main' : 'error.main',
                          sx: { fontSize: '0.7rem' },
                        }}
                      />
                      <Switch
                        edge="end"
                        checked={aggregator.isActive}
                        onChange={(e) => handleChannelToggle(aggregator, e.target.checked)}
                        disabled={submitting || !channelSettings.acceptingOnlineOrders}
                        color="success"
                        size="small"
                      />
                    </ListItem>
                    {index < activeAggregators.length - 1 && (
                      <Divider sx={{ ml: 7, mr: 2 }} />
                    )}
                  </React.Fragment>
                ))}
                
                {activeAggregators.length === 0 && (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      No delivery channels configured
                    </Typography>
                  </Box>
                )}
              </List>
            </Box>
            
            {/* Bottom spacer for safe scrolling */}
            <Box sx={{ height: 24 }} />
          </>
        )}
      </Box>

      {/* Online Orders Confirmation Dialog */}
      <BottomSheet
        open={onlineOrdersConfirmOpen}
        onClose={() => setOnlineOrdersConfirmOpen(false)}
        title={pendingOnlineOrdersValue ? 'Enable Online Orders?' : 'Disable Online Orders?'}
        subtitle={pendingOnlineOrdersValue 
          ? 'Your store will start accepting online orders'
          : 'Your store will stop accepting online orders'
        }
        actions={
          <BottomSheetActions
            onCancel={() => setOnlineOrdersConfirmOpen(false)}
            onConfirm={handleOnlineOrdersConfirm}
            confirmText={pendingOnlineOrdersValue ? 'Enable' : 'Disable'}
            confirmColor={pendingOnlineOrdersValue ? 'success' : 'error'}
            confirmDisabled={submitting || (!pendingOnlineOrdersValue && !ooReactivateAt)}
            confirmLoading={submitting}
            confirmIcon={pendingOnlineOrdersValue ? <OnlineIcon /> : <PauseIcon />}
          />
        }
      >
        <Stack spacing={2.5}>
          <Alert
            severity={pendingOnlineOrdersValue ? 'info' : 'warning'}
            icon={pendingOnlineOrdersValue ? <OnlineIcon /> : <WarningIcon />}
            sx={{ borderRadius: 2 }}
          >
            <Typography variant="body2">
              {pendingOnlineOrdersValue
                ? 'Customers will be able to place orders online. Make sure your store is ready to fulfill orders.'
                : 'Disabling Online Orders will also pause all active delivery aggregators (Deliveroo, Just Eat, Uber Eats, etc.). Customers will not be able to place orders through any channel until it turns back on.'
              }
            </Typography>
          </Alert>

          {!pendingOnlineOrdersValue && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
                TURN BACK ON AFTER
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
                {quickExpiryOptions.map((opt) => (
                  <Chip
                    key={opt.key}
                    label={opt.label}
                    size="small"
                    variant={ooQuickOption === opt.key ? 'filled' : 'outlined'}
                    color={ooQuickOption === opt.key ? 'primary' : 'default'}
                    onClick={() => {
                      setOoReactivateAt(opt.getValue());
                      setOoQuickOption(opt.key);
                    }}
                  />
                ))}
              </Stack>
              {/* Preset chips only — no free date/time entry, so the expiry can
                  never land past the 5 AM ceiling. */}
              {ooReactivateAt && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Online Orders will automatically turn back ON at{' '}
                  {formatSnoozeDateTime(ooReactivateAt, use24HourFormat)}.
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </BottomSheet>

      {/* Pause Channel Dialog */}
      <BottomSheet
        open={pauseDialogOpen}
        onClose={() => setPauseDialogOpen(false)}
        title={`Pause ${selectedChannel?.name || 'Channel'}`}
        subtitle="Temporarily stop receiving orders"
        actions={
          <BottomSheetActions
            onCancel={() => setPauseDialogOpen(false)}
            onConfirm={handlePauseConfirm}
            confirmText="Pause Channel"
            confirmColor="error"
            confirmDisabled={submitting || !pauseExpiry}
            confirmLoading={submitting}
            confirmIcon={<PauseIcon />}
          />
        }
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
              REASON (OPTIONAL)
            </Typography>
            <TextField
              value={pauseReason}
              onChange={(e) => setPauseReason(e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., Equipment issues, Staff shortage"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ mb: 1, display: 'block' }}>
              RESUME AFTER
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.5 }}>
              {quickExpiryOptions.map((opt) => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  size="small"
                  variant={selectedQuickOption === opt.key ? 'filled' : 'outlined'}
                  color={selectedQuickOption === opt.key ? 'primary' : 'default'}
                  onClick={() => {
                    setPauseExpiry(opt.getValue());
                    setSelectedQuickOption(opt.key);
                  }}
                />
              ))}
            </Stack>
            {/* Preset chips only — no free date/time entry, so the expiry can
                never land past the 5 AM ceiling. */}
            {pauseExpiry && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Resumes: {formatSnoozeDateTime(pauseExpiry, use24HourFormat)}
              </Typography>
            )}
          </Box>

          <Alert 
            severity="warning" 
            icon={<WarningIcon />}
            sx={{ borderRadius: 2 }}
          >
            <Typography variant="caption">
              Orders will not be received through {selectedChannel?.name} until resumed.
            </Typography>
          </Alert>
        </Stack>
      </BottomSheet>
    </Box>
  );
};
